import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject, MouseEvent as ReactMouseEvent, PointerEvent } from 'react';
import {
  Box,
  Typography,
  CardMedia,
  IconButton,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Product } from '@/types/product';
import type { NewProductsZoneCategory } from '@/types/newProductsZone';
import type { Category } from '@/types/category';
import { getProductName, getBrandName } from '@/utils/productUtils';
import { newProductsApi } from '@/api/newProducts';
import { useBrands } from '@/contexts/BrandContext';
import { enhanceProductsWithBrands } from '@/utils/brandUtils';
import { getCategoryName } from '@/utils/categoryUtils';

const PRODUCTS_PER_SLIDE = 6;
const CATEGORY_CAP = 20;
const COMPACT_PRODUCT_GRID_QUERY = '(max-width:800px)';
/** px/ms — quick flick advances slide like storefront carousels */
const CAROUSEL_FLICK_VELOCITY = 0.28;
/** px — delay pointer capture until drag so product/brand clicks still fire */
const CAROUSEL_DRAG_THRESHOLD = 4;

function ZoneProductCard({
  product,
  suppressClickRef,
}: {
  product: Product;
  suppressClickRef: MutableRefObject<boolean>;
}) {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);

  const brandName =
    getBrandName(product, i18n.language) || product.brandInfo?.name || product.brand || product.vendor || '';

  const goToProduct = () => {
    if (suppressClickRef.current) return;
    navigate(`/product/${product.id}`, { state: { product } });
  };

  const goToBrand = (e: ReactMouseEvent) => {
    e.stopPropagation();
    if (suppressClickRef.current) return;
    if (!product.brandId) return;
    navigate(`/brand/${product.brandId}/${encodeURIComponent(brandName || 'Brand')}`);
  };

  return (
    <Box
      data-zone-product-card
      onClick={goToProduct}
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <CardMedia
        component="img"
        draggable={false}
        image={imageError || !product.imageUrl ? '/images/img_placeholder.svg' : product.imageUrl}
        alt={product.name}
        onError={() => setImageError(true)}
        sx={{
          width: '100%',
          aspectRatio: '3/4',
          objectFit: 'cover',
          borderRadius: 0.5,
        }}
      />
      <Box sx={{ pt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {brandName && (
          <Typography
            onClick={goToBrand}
            sx={{
              color: '#17110D',
              fontSize: { xs: '11px', sm: '12px', md: '13px' },
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: product.brandId ? 'pointer' : 'default',
              transition: 'opacity 0.2s ease',
              '&:hover': {
                opacity: product.brandId ? 0.7 : 1,
              },
            }}
          >
            {brandName}
          </Typography>
        )}
        <Typography
          sx={{
            color: '#3D3C3C',
            fontSize: { xs: '11px', sm: '12px', md: '13px' },
            fontWeight: 400,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.35,
            minHeight: { xs: '2.7em', md: '2.6em' },
            '&:hover': {
              color: theme.palette.custom.brown,
            },
          }}
        >
          {getProductName(product, i18n.language)}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
          {product.comparePrice != null && product.comparePrice > 0 && (
            <Typography
              sx={{
                textDecoration: 'line-through',
                color: theme.palette.custom.brown,
                fontSize: { xs: '10px', md: '11px' },
              }}
            >
              HK${product.comparePrice.toFixed(2)}
            </Typography>
          )}
          <Typography sx={{ color: '#B31818', fontSize: { xs: '12px', md: '13px' } }} fontWeight={700}>
            HK${product.price.toFixed(2)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function NewProductsZoneCarousel({ categories, loading }: { categories: NewProductsZoneCategory[]; loading: boolean }) {
  const { t } = useTranslation();
  const isCompactProductGrid = useMediaQuery(COMPACT_PRODUCT_GRID_QUERY);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const categoryScrollerRef = useRef<HTMLDivElement>(null);
  const categoryItemRefs = useRef<(HTMLElement | null)[]>([]);
  const carouselDragRef = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startScroll: 0,
    moved: false,
  });
  const suppressProductClickRef = useRef(false);
  const dragSamplesRef = useRef<{ x: number; t: number }[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isCarouselDragging, setIsCarouselDragging] = useState(false);
  const [categoryScroll, setCategoryScroll] = useState({ left: false, right: false });
  const [categoryOverflows, setCategoryOverflows] = useState(false);
  /** Target index during programmatic scroll — blocks observer from picking intermediate slides */
  const programmaticTargetRef = useRef<number | null>(null);

  const slideCount = categories.length;
  const safeIndex = Math.min(activeIndex, Math.max(0, slideCount - 1));
  const categoryIdsKey = useMemo(() => categories.map((c) => c.id).join(','), [categories]);

  useEffect(() => {
    setActiveIndex(0);
    const el = scrollerRef.current;
    if (el) el.scrollLeft = 0;
    const categoryEl = categoryScrollerRef.current;
    if (categoryEl) categoryEl.scrollLeft = 0;
  }, [categoryIdsKey]);

  const updateCategoryScrollState = useCallback(() => {
    const el = categoryScrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCategoryOverflows(maxScroll > 1);
    setCategoryScroll({
      left: el.scrollLeft > 1,
      right: el.scrollLeft < maxScroll - 1,
    });
  }, []);

  useLayoutEffect(() => {
    const el = categoryScrollerRef.current;
    if (!el) return;
    updateCategoryScrollState();
    el.addEventListener('scroll', updateCategoryScrollState, { passive: true });
    const ro = new ResizeObserver(updateCategoryScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateCategoryScrollState);
      ro.disconnect();
    };
  }, [categoryIdsKey, slideCount, updateCategoryScrollState]);

  const scrollCategoryStrip = useCallback((direction: 'left' | 'right') => {
    const el = categoryScrollerRef.current;
    if (!el) return;
    const firstItem = categoryItemRefs.current[0];
    const itemWidth = firstItem?.offsetWidth ?? 88;
    const amount = Math.max(el.clientWidth * 0.55, itemWidth * 2);
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  /** Scroll selected category tab to the horizontal center of the strip */
  const centerCategoryTab = useCallback((index: number, behavior: ScrollBehavior = 'smooth') => {
    const scroller = categoryScrollerRef.current;
    const item = categoryItemRefs.current[index];
    if (!scroller || !item) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 0) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const itemCenter = itemRect.left + itemRect.width / 2;
    const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
    const delta = itemCenter - scrollerCenter;

    if (Math.abs(delta) < 2) return;

    const targetScrollLeft = Math.max(0, Math.min(maxScroll, scroller.scrollLeft + delta));
    scroller.scrollTo({ left: targetScrollLeft, behavior });
  }, []);

  const scrollToIndex = useCallback(
    (index: number) => {
      const el = scrollerRef.current;
      if (!el || slideCount === 0) return;
      const clamped = Math.max(0, Math.min(index, slideCount - 1));
      programmaticTargetRef.current = clamped;
      setActiveIndex(clamped);
      centerCategoryTab(clamped, 'smooth');
      el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
    },
    [slideCount, centerCategoryTab]
  );

  const snapCarouselAfterRelease = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || slideCount === 0) return;
    const w = el.clientWidth;
    if (w <= 0) return;

    const samples = dragSamplesRef.current;
    let vx = 0;
    if (samples.length >= 2) {
      const end = samples[samples.length - 1];
      const start = samples[Math.max(0, samples.length - 6)];
      const dt = end.t - start.t;
      if (dt >= 8) vx = (end.x - start.x) / dt;
    }

    const frac = el.scrollLeft / w;
    let idx: number;
    if (vx < -CAROUSEL_FLICK_VELOCITY) idx = Math.ceil(frac - 0.001);
    else if (vx > CAROUSEL_FLICK_VELOCITY) idx = Math.floor(frac + 0.001);
    else idx = Math.round(frac);

    const clamped = Math.max(0, Math.min(slideCount - 1, idx));
    programmaticTargetRef.current = clamped;
    setActiveIndex(clamped);
    centerCategoryTab(clamped, 'smooth');
    el.scrollTo({ left: clamped * w, behavior: 'smooth' });
    dragSamplesRef.current = [];
  }, [slideCount, centerCategoryTab]);

  const onCarouselPointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const el = scrollerRef.current;
    if (!el) return;
    carouselDragRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      moved: false,
    };
    dragSamplesRef.current = [{ x: e.clientX, t: Date.now() }];
    // Do not setPointerCapture yet — wait until drag threshold so card clicks still fire
  }, []);

  const onCarouselPointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const s = carouselDragRef.current;
    if (!s.active || s.pointerId !== e.pointerId || e.pointerType !== 'mouse') return;
    const dx = e.clientX - s.startX;
    const el = scrollerRef.current;
    if (!el) return;

    const now = Date.now();
    dragSamplesRef.current.push({ x: e.clientX, t: now });
    if (dragSamplesRef.current.length > 10) dragSamplesRef.current.shift();

    if (Math.abs(dx) > CAROUSEL_DRAG_THRESHOLD) {
      if (!s.moved) {
        s.moved = true;
        setIsCarouselDragging(true);
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
      el.scrollLeft = s.startScroll - dx;
    }
  }, []);

  const endCarouselPointerDrag = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      const s = carouselDragRef.current;
      if (!s.active || s.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      carouselDragRef.current = {
        active: false,
        pointerId: -1,
        startX: 0,
        startScroll: 0,
        moved: false,
      };
      setIsCarouselDragging(false);
      if (s.moved) {
        suppressProductClickRef.current = true;
        window.setTimeout(() => {
          suppressProductClickRef.current = false;
        }, 0);
        snapCarouselAfterRelease();
      } else {
        dragSamplesRef.current = [];
      }
    },
    [snapCarouselAfterRelease]
  );

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root || slideCount === 0) return;

    const clearProgrammaticTargetIfArrived = () => {
      const target = programmaticTargetRef.current;
      if (target === null) return;
      const w = root.clientWidth;
      if (w <= 0) return;
      const current = Math.round(root.scrollLeft / w);
      if (current === target) {
        programmaticTargetRef.current = null;
      }
    };

    let scrollSettleTimer: number | undefined;
    const onScroll = () => {
      window.clearTimeout(scrollSettleTimer);
      scrollSettleTimer = window.setTimeout(clearProgrammaticTargetIfArrived, 120);
    };

    root.addEventListener('scrollend', clearProgrammaticTargetIfArrived);
    root.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      root.removeEventListener('scrollend', clearProgrammaticTargetIfArrived);
      root.removeEventListener('scroll', onScroll);
      window.clearTimeout(scrollSettleTimer);
    };
  }, [slideCount, categoryIdsKey]);

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root || slideCount === 0) return;

    const slides = root.querySelectorAll<HTMLElement>('[data-new-zone-slide]');
    const observer = new IntersectionObserver(
      (entries) => {
        if (programmaticTargetRef.current !== null) return;
        let best: { idx: number; ratio: number } | null = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const idx = Number(entry.target.getAttribute('data-slide-index'));
          if (Number.isNaN(idx)) continue;
          const ratio = entry.intersectionRatio;
          if (ratio >= 0.45 && (!best || ratio > best.ratio)) best = { idx, ratio };
        }
        if (best) {
          const nextIndex = best.idx;
          setActiveIndex((prev) => {
            if (prev !== nextIndex) {
              requestAnimationFrame(() => centerCategoryTab(nextIndex, 'smooth'));
            }
            return nextIndex;
          });
        }
      },
      { root, rootMargin: '0px', threshold: [0.45, 0.55, 0.65, 0.75, 0.85, 0.95, 1] }
    );

    slides.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [slideCount, categoryIdsKey, centerCategoryTab]);

  if (loading && slideCount === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: 'lg',
          mx: 'auto',
          px: { xs: '15px', md: 2 },
          py: { xs: 3, md: 5 },
          bgcolor: '#fff',
        }}
      >
        <Skeleton variant="text" sx={{ fontSize: '1.35rem', maxWidth: 280, mx: 'auto', mb: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={64} height={64} />
              <Skeleton variant="text" width={72} height={14} />
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isCompactProductGrid
              ? 'repeat(3, minmax(0, 1fr))'
              : `repeat(${PRODUCTS_PER_SLIDE}, minmax(0, 1fr))`,
            columnGap: 2,
            rowGap: isCompactProductGrid ? 2 : 0,
          }}
        >
          {Array.from({ length: PRODUCTS_PER_SLIDE }).map((_, i) => (
            <Box key={i}>
              <Skeleton variant="rectangular" sx={{ width: '100%', aspectRatio: '3/4', borderRadius: 1 }} />
              <Skeleton sx={{ mt: 1 }} />
              <Skeleton width="80%" />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (slideCount === 0) return null;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 'lg',
        mx: 'auto',
        px: { xs: '15px', md: 2 },
        py: { xs: 3, md: 5 },
        bgcolor: '#fff',
      }}
    >
      <Typography
        component="h2"
        sx={{
          textAlign: 'center',
          fontWeight: 800,
          fontSize: { xs: '1.1rem', md: '1.35rem' },
          letterSpacing: '0.08em',
          mb: { xs: 3, md: 4 },
        }}
      >
        {t('newProductsZone.title')}
      </Typography>

      <Box
        sx={{
          position: 'relative',
          borderBottom: '1px solid #e8e8e8',
          mb: 3,
        }}
      >
        <IconButton
          aria-label={t('newProductsZone.categoryScrollPrev')}
          onClick={() => scrollCategoryStrip('left')}
          disabled={!categoryScroll.left}
          size="small"
          sx={{
            position: 'absolute',
            left: { xs: -6, md: -2 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            border: '1px solid #ddd',
            bgcolor: '#fff',
            width: 32,
            height: 32,
            '&.Mui-disabled': { opacity: 0.35 },
          }}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
        </IconButton>

        <Box
          ref={categoryScrollerRef}
          sx={{
            display: 'flex',
            justifyContent: categoryOverflows ? 'flex-start' : 'center',
            gap: { xs: 2, sm: 3, md: 4 },
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 0,
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            px: { xs: 4.5, md: 5 },
          }}
        >
          {categories.map((cat, index) => {
            const active = index === safeIndex;
            return (
              <Box
                key={cat.id}
                component="button"
                type="button"
                ref={(el) => {
                  categoryItemRefs.current[index] = el as HTMLElement | null;
                }}
                onClick={() => scrollToIndex(index)}
                sx={{
                  flex: '0 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 72,
                  maxWidth: 96,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  p: 0,
                  pb: 1.5,
                  borderBottom: active ? '3px solid #17110D' : '3px solid transparent',
                  mb: '-1px',
                  color: 'inherit',
                }}
              >
                <Box
                  component="img"
                  src={cat.imageUrl}
                  alt=""
                  sx={{
                    width: { xs: 56, md: 64 },
                    height: { xs: 56, md: 64 },
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: active ? '2px solid #17110D' : '1px solid #e0e0e0',
                    boxSizing: 'border-box',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: '9px', sm: '10px', md: '11px' },
                    fontWeight: 700,
                    textAlign: 'center',
                    lineHeight: 1.2,
                    letterSpacing: '0.02em',
                    maxWidth: 100,
                  }}
                >
                  {cat.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <IconButton
          aria-label={t('newProductsZone.categoryScrollNext')}
          onClick={() => scrollCategoryStrip('right')}
          disabled={!categoryScroll.right}
          size="small"
          sx={{
            position: 'absolute',
            right: { xs: -6, md: -2 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            border: '1px solid #ddd',
            bgcolor: '#fff',
            width: 32,
            height: 32,
            '&.Mui-disabled': { opacity: 0.35 },
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ position: 'relative' }}>
        <Box
          ref={scrollerRef}
          onPointerDown={onCarouselPointerDown}
          onPointerMove={onCarouselPointerMove}
          onPointerUp={endCarouselPointerDrag}
          onPointerCancel={endCarouselPointerDrag}
          onDragStart={(e) => e.preventDefault()}
          onPointerLeave={(e) => {
            if (carouselDragRef.current.active && carouselDragRef.current.pointerId === e.pointerId) {
              endCarouselPointerDrag(e);
            }
          }}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            scrollSnapType: isCarouselDragging ? 'none' : 'x mandatory',
            scrollBehavior: isCarouselDragging ? 'auto' : 'smooth',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            mx: { xs: -0.5, md: 0 },
            cursor: isCarouselDragging ? 'grabbing' : 'grab',
            touchAction: 'pan-x',
            userSelect: isCarouselDragging ? 'none' : 'auto',
            borderRadius: 1,
            outlineOffset: 2,
            transition: 'outline-color 0.15s ease',
          }}
        >
          {categories.map((cat, slideIdx) => (
            <Box
              key={cat.id}
              data-new-zone-slide
              data-slide-index={slideIdx}
              sx={{
                flex: '0 0 100%',
                width: '100%',
                scrollSnapAlign: 'start',
                scrollSnapStop: 'always',
                boxSizing: 'border-box',
                px: { xs: 0.5, md: 0 },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isCompactProductGrid
                    ? 'repeat(3, minmax(0, 1fr))'
                    : `repeat(${PRODUCTS_PER_SLIDE}, minmax(0, 1fr))`,
                  columnGap: isCompactProductGrid ? 1 : { xs: 1, sm: 1.5, md: 2 },
                  rowGap: isCompactProductGrid ? 1.5 : 0,
                }}
              >
                {cat.products.slice(0, PRODUCTS_PER_SLIDE).map((p) => (
                  <ZoneProductCard key={p.id} product={p} suppressClickRef={suppressProductClickRef} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 3,
            gap: 2,
          }}
        >
          <IconButton
            aria-label={t('newProductsZone.prev')}
            onClick={() => scrollToIndex(safeIndex - 1)}
            disabled={safeIndex <= 0}
            size="small"
            sx={{
              border: '1px solid #ddd',
              bgcolor: '#fff',
              '&.Mui-disabled': { opacity: 0.4 },
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 18, ml: 0.5 }} />
          </IconButton>
          <Typography sx={{ fontSize: 14, color: '#666', fontVariantNumeric: 'tabular-nums' }}>
            {safeIndex + 1} / {slideCount}
          </Typography>
          <IconButton
            aria-label={t('newProductsZone.next')}
            onClick={() => scrollToIndex(safeIndex + 1)}
            disabled={safeIndex >= slideCount - 1}
            size="small"
            sx={{
              border: '1px solid #ddd',
              bgcolor: '#fff',
              '&.Mui-disabled': { opacity: 0.4 },
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export function NewProductsZone() {
  const { i18n } = useTranslation();
  const { brands } = useBrands();
  const [rawCategories, setRawCategories] = useState<NewProductsZoneCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const { categories: zoneCategories } = await newProductsApi.getNewProductsZone({
          categoryCap: CATEGORY_CAP,
          minProducts: 1,
          productsPerCategory: PRODUCTS_PER_SLIDE,
        });
        if (cancelled) return;

        const mapped: NewProductsZoneCategory[] = zoneCategories.map(({ category: cat, products }) => ({
          id: String(cat.id),
          label: getCategoryName(cat as unknown as Category, i18n.language),
          imageUrl: cat.imageUrl ?? '/images/img_placeholder.svg',
          products: products.slice(0, PRODUCTS_PER_SLIDE) as Product[],
        }));

        setRawCategories(mapped);
      } catch {
        if (!cancelled) setRawCategories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);
  const categories = useMemo(() => {
    if (brands.length === 0) return rawCategories;
    return rawCategories.map((z) => ({
      ...z,
      products: enhanceProductsWithBrands(z.products, brands),
    }));
  }, [rawCategories, brands]);

  return <NewProductsZoneCarousel categories={categories} loading={loading} />;
}

export default NewProductsZone;
